import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    FaLock, FaCheckCircle, FaChevronDown, FaChevronUp,
    FaCalendarAlt, FaGasPump, FaPalette, FaShieldAlt,
    FaMoneyBillWave, FaCarCrash, FaUsers, FaRobot, FaIdCard,
    FaInstagram, FaCommentAlt, FaSearch, FaArrowRight
} from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import './styling/FullMotCheck.css'

export default function FullMotCheck() {
    const [searchParams] = useSearchParams();
    const [motData, setMotData] = useState(null);
    const [showMOT, setShowMOT] = useState(true);
    const [loading, setLoading] = useState(false);
    const [expandedTests, setExpandedTests] = useState({});
    const [elapsed, setElapsed] = useState(0);
    const [checkoutElapsed, setCheckoutElapsed] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, []);

    useEffect(() => {
        if (motData !== null) return;
        const interval = setInterval(() => setElapsed(e => e + 1), 1000);
        return () => clearInterval(interval);
    }, [motData]);

    useEffect(() => {
        if (!loading) return;
        const interval = setInterval(() => setCheckoutElapsed(e => e + 1), 1000);
        return () => clearInterval(interval);
    }, [loading]);

    let reg = searchParams.get("reg");

    useEffect(() => {
        const getCarDetails = async () => {
            let response = await fetch(
                process.env.REACT_APP_BASE_URL + '/Vehicle/dvlamot?registration=' + reg, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                var motResponse = await response.json()
                if (motResponse.registration == null) {
                    navigate(`/?carnotfound=true`)
                } else {
                    setMotData(motResponse)
                }
            } else {
                navigate(`/?carnotfound=true`)
            }
        }
        getCarDetails()
    }, [])

    const addOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return `${day}th`;
        switch (day % 10) {
            case 1: return `${day}st`;
            case 2: return `${day}nd`;
            case 3: return `${day}rd`;
            default: return `${day}th`;
        }
    };

    const extractDayFromDateStr = (dateStr) => {
        const [year, month, day] = dateStr.split('-');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${addOrdinalSuffix(parseInt(day, 10))} ${months[parseInt(month, 10) - 1]} ${year}`;
    };

    const formatDate = (date) => {
        const d = new Date(date);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${addOrdinalSuffix(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}`;
    };

    const goToCheckout = async () => {
        let response = await fetch(
            process.env.REACT_APP_BASE_URL + '/Checkout?registration=' + reg, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        });
        var responseData = await response.json();
        window.location.href = responseData.url;
    }

    const toggleTest = (index) => {
        setExpandedTests(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const getMotStatus = () => {
        if (!motData.motTests || motData.motTests.length === 0) {
            if (motData.registrationDate) {
                const regDate = new Date(motData.registrationDate);
                const threeYearsAgo = new Date();
                threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
                if (regDate > threeYearsAgo) return 'NOT YET DUE';
            }
            return 'NO DATA';
        }
        const { testResult, daysLeft } = motData.motTests[0];
        if (daysLeft < 0) return 'EXPIRED';
        if (testResult !== 'PASSED') return 'FAILED';
        return 'PASSED';
    };

    const motLoadingMessage = elapsed < 2 ? 'Getting your MOT history'
        : elapsed < 4 ? 'Checking MOT records'
        : 'Almost there';

    const checkoutLoadingMessage = checkoutElapsed < 2 ? 'Preparing your full report'
        : checkoutElapsed < 4 ? 'Setting up secure checkout'
        : 'Almost there';

    if (motData === null || loading) {
        const loadingMessage = loading ? checkoutLoadingMessage : motLoadingMessage;
        return (
            <div className='loading'>
                <p className='loading-text'>
                    {loadingMessage}
                    <span className='loading-dot'>.</span>
                    <span className='loading-dot'>.</span>
                    <span className='loading-dot'>.</span>
                </p>
            </div>
        );
    }

    const motStatus = getMotStatus();

    return (
        <div className='mot-page'>

            {/* Header */}
            <div className='mot-header'>
                <p className='numberplate'>{reg}</p>
                <h1 className='mot-title'>{motData.make} {motData.model}</h1>
                <div className='mot-details-bar'>
                    {motData.registrationDate && (
                        <span className='mot-detail-item'>
                            <FaCalendarAlt /> {extractDayFromDateStr(motData.registrationDate)}
                        </span>
                    )}
                    {motData.primaryColour && (
                        <span className='mot-detail-item'>
                            <FaPalette /> {motData.primaryColour}
                        </span>
                    )}
                    {motData.fuelType && (
                        <span className='mot-detail-item'>
                            <FaGasPump /> {motData.fuelType}
                        </span>
                    )}
                    {motData.motTests?.[0]?.daysLeft > 0 && (
                        <span className='mot-days-badge'>
                            {motData.motTests[0].daysLeft} days left
                        </span>
                    )}
                </div>
                <button className='mot-cta-button' onClick={() => { goToCheckout(); setLoading(true); }}>
                    Unlock Full Vehicle Check — £7.99
                </button>
                <p className='mot-cta-sub'>Instant report · Secure checkout</p>
            </div>

            {/* Status Cards */}
            <div className='mot-status-cards'>
                <div className='status-card'>
                    <FaCheckCircle className='status-card-icon green' />
                    <span className='status-card-label'>MOT Status</span>
                    <span className={`status-card-value ${motStatus === 'PASSED' ? 'green' : motStatus === 'NOT YET DUE' ? 'blue' : 'red'}`}>{motStatus}</span>
                    {motStatus === 'NOT YET DUE' && <span className='status-card-sub'>Under 3 years old</span>}
                </div>
                <div className='status-card'>
                    <span className='status-card-number'>{motData.outstandingAdvisories ?? 0}</span>
                    <span className='status-card-label'>Outstanding Advisories</span>
                    <span className='status-card-sub'>{motData.outstandingAdvisories === 0 ? 'None recorded' : 'See history'}</span>
                </div>
                <div className='status-card locked'>
                    <FaLock className='status-card-icon grey' />
                    <span className='status-card-label'>Finance Check</span>
                    <span className='status-card-sub'>Full report</span>
                </div>
                <div className='status-card locked'>
                    <FaLock className='status-card-icon grey' />
                    <span className='status-card-label'>Write-Off Check</span>
                    <span className='status-card-sub'>Full report</span>
                </div>
                <div className='status-card locked'>
                    <FaLock className='status-card-icon grey' />
                    <span className='status-card-label'>Stolen Check</span>
                    <span className='status-card-sub'>Full report</span>
                </div>
            </div>

            {/* Notices */}
            {(motData.outstandingAdvisories > 0 || motData.corroded || motData.oilLeak) && (
                <div className='mot-notices'>
                    {motData.outstandingAdvisories > 0 && (
                        <a href="/advisories" className='mot-notice-link'>
                            ⚠️ {motData.outstandingAdvisories} outstanding {motData.outstandingAdvisories === 1 ? 'advisory' : 'advisories'} — find out more
                        </a>
                    )}
                    {motData.corroded && (
                        <a href="/motrust" className='mot-notice-link'>
                            ⚠️ This car has had structural corrosion problems — find out more
                        </a>
                    )}
                    {motData.oilLeak && (
                        <a href="/oilleaks" className='mot-notice-link'>
                            ⚠️ This car has experienced past oil leaks — find out more
                        </a>
                    )}
                </div>
            )}

            {/* Instagram promo card */}
            <div className='insta-card'>
                <div className='insta-card-top'>
                    <FaInstagram className='insta-icon' />
                    <div>
                        <p className='insta-title'>Thinking of buying this car?</p>
                        <p className='insta-subtitle'>Send us the advert on Instagram and we'll take a look at it for you — free.</p>
                    </div>
                </div>
                <div className='insta-bullets'>
                    <div className='insta-bullet'><FaCommentAlt className='insta-bullet-icon' /><span>Get a second opinion on the price</span></div>
                    <div className='insta-bullet'><FaSearch className='insta-bullet-icon' /><span>We'll check the details and history</span></div>
                    <div className='insta-bullet'><FaShieldAlt className='insta-bullet-icon' /><span>Spot any potential red flags</span></div>
                </div>
                <a className='insta-button' href="https://www.instagram.com/carinformation.co.uk/" target="_blank" rel="noreferrer">
                    <FaInstagram /> Send us the advert on Instagram <FaArrowRight />
                </a>
                <p className='insta-small'>No purchase required. We'll reply on Instagram.</p>
            </div>

            {/* MOT History */}
            {motData.motTests && motData.motTests.length > 0 && (
                <div className='mot-history-section'>
                    <div className='mot-history-header' onClick={() => setShowMOT(!showMOT)}>
                        <h3>MOT History</h3>
                        {showMOT ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                    {showMOT && (
                        <div className='mot-history-list'>
                            {motData.motTests.map((record, index) => (
                                <div key={index} className='mot-history-item'>
                                    <div className='mot-history-item-header' onClick={() => toggleTest(index)}>
                                        <span className={`mot-badge ${record.testResult === 'PASSED' ? 'passed' : 'failed'}`}>
                                            {record.testResult}
                                        </span>
                                        <span className='mot-history-date'>{extractDayFromDateStr(record.completedDate)}</span>
                                        <span className='mot-history-mileage'>{Number(record.odometerValue).toLocaleString()} {record.odometerUnit}</span>
                                        {record.defects?.length > 0 && (
                                            expandedTests[index] ? <FaChevronUp className='mot-chevron' /> : <FaChevronDown className='mot-chevron' />
                                        )}
                                    </div>
                                    {expandedTests[index] && record.defects?.length > 0 && (
                                        <div className='mot-history-defects'>
                                            {record.defects.map((defect, i) => (
                                                <p key={i} className='mot-defect'>
                                                    <span className={`defect-type ${defect.type?.toLowerCase()}`}>{defect.type}</span>
                                                    {defect.text}
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Mileage graph */}
            {motData.motTests && motData.motTests.length > 3 && (() => {
                const chartData = [...motData.motTests]
                    .filter(r => r.odometerValue > 0)
                    .sort((a, b) => new Date(a.completedDate) - new Date(b.completedDate))
                    .map(r => {
                        const [y, m] = r.completedDate.split('-');
                        return { date: `${m}/${y.slice(2)}`, mileage: Number(r.odometerValue) };
                    });
                return (
                    <div className='mot-history-section'>
                        <div className='mot-history-header' style={{ cursor: 'default' }}>
                            <h3>Mileage History</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} />
                                <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: '#6b7280' }} width={36} />
                                <Tooltip formatter={v => Number(v).toLocaleString() + ' mi'} labelStyle={{ color: '#111827', fontWeight: 600 }} />
                                <Line type="monotone" dataKey="mileage" stroke="#16a34a" strokeWidth={2} dot={{ r: 3, fill: '#16a34a' }} activeDot={{ r: 5 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                );
            })()}

            {/* New car with no MOT history */}
            {(!motData.motTests || motData.motTests.length === 0) && motData.motTestDueDate && (
                <div className='mot-history-section'>
                    <p className='mot-new-car'>No MOT history recorded — MOT due {formatDate(motData.motTestDueDate)}</p>
                </div>
            )}

            {/* What's included */}
            <div className='mot-included-section'>
                <h3>What's included in the Full Report</h3>
                <div className='mot-included-grid'>
                    <div className='mot-included-item'><FaRobot className='included-icon' /><span>AI Overview</span></div>
                    <div className='mot-included-item'><FaMoneyBillWave className='included-icon' /><span>Outstanding Finance</span></div>
                    <div className='mot-included-item'><FaCarCrash className='included-icon' /><span>Write-Off Check</span></div>
                    <div className='mot-included-item'><FaUsers className='included-icon' /><span>Previous Keepers</span></div>
                    <div className='mot-included-item'><FaShieldAlt className='included-icon' /><span>Stolen Check</span></div>
                    <div className='mot-included-item'><FaIdCard className='included-icon' /><span>Last 5 Digits of VIN</span></div>
                </div>
                <button className='mot-cta-button' onClick={() => { goToCheckout(); setLoading(true); }}>
                    Get Full Report — £7.99
                </button>
                <br />
                <br />
                <a href="https://www.carinformation.co.uk/samplecheck">View Full Report Sample</a>
            </div>

        </div>
    );
}
